import type { HeadersFunction, LoaderFunctionArgs, MetaFunction } from 'react-router'
import { Link, useLoaderData } from 'react-router'

import social from '@/assets/social.png?url'
import Logo from '@/components/Logo'
import SiteFooter from '@/components/SiteFooter'
import SiteHeader from '@/components/SiteHeader'
import { findCaseStudies } from '@/models/case-study.server'
import { pageHelper } from '@/request'
import { caseStudyLabel } from '@/utils/case-studies'

type LoaderData = typeof loader

export async function loader({ url }: LoaderFunctionArgs) {
  const { root } = pageHelper(url)

  const caseStudies = findCaseStudies()

  return {
    caseStudies: caseStudies,
    root: root
  }
}

export const headers: HeadersFunction = ({ parentHeaders }) => ({
  'Cache-Control': parentHeaders.get('Cache-Control') ?? ''
})

export const meta: MetaFunction<LoaderData> = ({ loaderData }) => {
  if (!loaderData) return []

  const { root } = loaderData

  const title = 'Case studies — PlanetScale'
  const description = 'Companies worldwide choose PlanetScale to transform their business, starting with their data.'
  const image = `${root}${social}`

  return [
    { title },
    { name: 'description', content: description },
    { property: 'og:url', content: 'https://planetscale.com/case-studies' },
    { property: 'og:type', content: 'website' },
    { property: 'og:title', content: title },
    { property: 'og:image', content: image },
    { property: 'og:description', content: description },
    { property: 'twitter:card', content: 'summary_large_image' },
    { property: 'twitter:site', content: '@PlanetScale' },
    { property: 'twitter:creator', content: '@PlanetScale' },
    { property: 'twitter:url', content: 'https://planetscale.com/case-studies' },
    { property: 'twitter:title', content: title },
    { property: 'twitter:description', content: description },
    { property: 'twitter:image', content: image },
    { tagName: 'link', rel: 'canonical', href: 'https://planetscale.com/case-studies' }
  ]
}

export default function CaseStudies() {
  const { caseStudies } = useLoaderData<LoaderData>()

  return (
    <>
      <SiteHeader />

      <main className='container mb-6 max-w-7xl px-3 sm:px-5 lg:px-12'>
        <section>
          <h1>Case studies</h1>

          <p>Companies worldwide choose PlanetScale to transform their business, starting with their data.</p>

          {caseStudies.map((caseStudy) => (
            <div key={caseStudy.slug}>
              <hr className='dot-fill' />

              <div className='flex flex-col gap-x-4 gap-y-3 md:flex-row'>
                <div className='flex shrink-0 pt-2 md:w-1/6 md:items-start md:justify-center'>
                  {caseStudy.logo && (
                    <Logo
                      name={caseStudy.logo}
                      ariaLabel={caseStudy.name}
                      className='block h-auto w-20 object-contain md:h-12 md:w-16 dark:hidden'
                    />
                  )}
                  {caseStudy.logoDarkmode && (
                    <Logo
                      name={caseStudy.logoDarkmode}
                      ariaLabel={caseStudy.name}
                      className='hidden h-auto w-20 object-contain md:h-12 md:w-16 dark:block'
                    />
                  )}
                </div>

                <div className='md:w-5/6'>
                  <div className='mb-2 flex flex-wrap items-baseline gap-2'>
                    <h2 className='mb-0'>
                      {caseStudy.external ? (
                        <a
                          className='hover:bg-gray-900 hover:text-white hover:no-underline dark:hover:bg-gray-50 dark:hover:text-black'
                          href={caseStudy.url}
                          rel='nofollow noopener noreferrer'
                          target='_blank'
                        >
                          {caseStudy.name} [External]
                        </a>
                      ) : (
                        <Link
                          className='hover:bg-gray-900 hover:text-white hover:no-underline dark:hover:bg-gray-50 dark:hover:text-black'
                          prefetch='intent'
                          to={`/case-studies/${caseStudy.slug}`}
                        >
                          {caseStudy.name}
                        </Link>
                      )}
                    </h2>
                    {caseStudy.type && <span className='font-medium text-green'>{caseStudyLabel(caseStudy.type)}</span>}
                  </div>

                  <p>
                    {caseStudy.excerpt}{' '}
                    {caseStudy.external ? (
                      <a
                        className='text-default bg-blue-100 px-sm hover:bg-blue-600 hover:text-white dark:bg-blue-900 dark:text-blue dark:hover:bg-blue-500 dark:hover:text-black'
                        href={caseStudy.url}
                        rel='nofollow noopener noreferrer'
                        target='_blank'
                      >
                        →
                      </a>
                    ) : (
                      <Link
                        className='text-default bg-blue-100 px-sm hover:bg-blue-600 hover:text-white dark:bg-blue-900 dark:text-blue dark:hover:bg-blue-500 dark:hover:text-black'
                        prefetch='intent'
                        to={`/case-studies/${caseStudy.slug}`}
                      >
                        →
                      </Link>
                    )}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </section>
      </main>

      <SiteFooter />
    </>
  )
}
